<template>
    <Progressbar v-if="loading && !opportunity.name" />
    <NetworkError v-else-if="!opportunity.name && errors" @tryAgain="getData" />
    <div class="w-full h-full bg-gray-200 dark:bg-base-100 rounded-tr-3xl xl:px-9 pt-6 lg:pt-10 pb-14 " v-else>
        <div class="container">
            <div class="flex justify-between items-center mb-6">
                <h3 class="flex items-center md:text-26 text-2xl dark:text-white text-biscay-700 font-semibold">
                    <i class="md:flex hidden w-2 h-2 dark:bg-white rounded-full bg-blue-700 ml-2"></i>
                    {{ __('Opportunity details') }}
                </h3>
                <div class="flex justify-end order-first lg:order-last" v-if="$route.params.id">
                    <Dropdown labelClass="btn-outline !btn-sm">
                        <ul class="menu dropdown-content bg-base-100 rounded-box z-[2] w-36 p-2 shadow lg:!end-auto">
                            <li>
                                <router-link :to="{ name: 'user.opportunity.edit', params: { id: opportunity.id } }"
                                    :class="{ 'btn-disabled opacity-50': !can('opportunity.edit.all') && !can('opportunity.edit.self', opportunity.self) }">
                                    {{ __('Edit') }}
                                </router-link>
                            </li>
                            <li v-if="can('appointment.create')">
                                <button @click="setAppointment('create')">
                                    {{ __('Create activity') }}
                                </button>
                            </li>
                            <li v-if="can('invoice.create')">
                                <button @click="setInvoice('create')">
                                    {{ __('Add invoice') }}
                                </button>
                            </li>
                        </ul>
                    </Dropdown>
                    <label for="note-sidebar" @click="openSidebar"
                        class="btn btn-outline btn-primary btn-sm mr-2">
                        {{ __('Note') }}
                    </label>
                    <label @click="setItem(opportunity)" for="opportunity-delete-modal"
                        :class="{ 'btn-disabled': !can('opportunity.delete.all') && !can('opportunity.delete.self', opportunity.self) }"
                        class="btn btn-outline btn-error btn-sm mr-2">
                        {{ __('Delete') }}
                    </label>
                </div>
            </div>
            <div class="bg-white dark:bg-base-300 dark:shadow-whiteShadow rounded-xl shadow-sm mb-4"
                v-if="opportunity.clue">
                <div class="collapse-title">
                    <InformationCircleIcon class="text-info w-6 h-6 ml-1 inline-block -mt-0.5" />
                    {{ __('Converted from clue') + ' ' }}
                    <router-link class="text-blue-500 hover:text-blue-600"
                        :to="{ name: 'user.clue.show', params: { id: opportunity.clue.id } }">{{ opportunity.clue.name
                        }}</router-link>
                    {{ ' ' + __('by') + ' ' + opportunity.clue.user + ' ' + __('In history') + ' ' +
                        opportunity.clue.updated_at }}
                </div>
            </div>
            <div class="bg-white dark:bg-base-300 dark:shadow-whiteShadow rounded-xl shadow-sm collapse-plus collapse">
                <input type="checkbox" checked />
                <div class="collapse-title text-xl font-medium">
                    {{ __('Opportunity information') }}
                </div>
                <div class="collapse-content">
                    <div class="grid md:grid-cols-2 grid-cols-1 lg:grid-cols-3 md:gap-x-14 gap-y-4">
                        <p class="text-gray-70">{{ __('Opportunity name') }}: <span
                                class="text-black dark:text-white">{{
                                    opportunity.name
                                }}</span></p>
                        <p class="text-gray-70">{{ __('Person') }}: <router-link
                                :to="{ name: 'user.person.show', params: { id: opportunity.person?.id } }"
                                class="text-blue-500 hover:text-blue-600">{{
                                    opportunity.person?.name || '---'
                                }}</router-link></p>
                        <p class="text-gray-70">{{ __('Account name') }}: <router-link v-if="opportunity.account?.id"
                                :to="{ name: 'user.account.show', params: { id: opportunity.account?.id } }"
                                class="text-blue-500 hover:text-blue-600">{{
                                    opportunity.account?.name
                                }}</router-link><span v-else class="text-black dark:text-white">---</span></p>
                        <p class="text-gray-70">{{ __('Amount') }}: <span class="text-black dark:text-white">{{
                            setCurrency(opportunity.amount) || '---' }}</span></p>
                        <p class="text-gray-70">{{ __('Sale route') }}: <span class="text-black dark:text-white">{{
                            opportunity.sale_route || '---' }}</span></p>
                        <p class="text-gray-70">{{ __('Sales stage') }}: <span class="text-black dark:text-white">{{
                            opportunity.sale_process || '---' }}</span></p>
                        <p class="text-gray-70">{{ __('Status') }}: <span class="text-black dark:text-white"
                                :class="{ '!text-success': opportunity.status == 'completed', '!text-error': opportunity.status == 'failed' }">{{
                                    __(opportunity.status?.capitalize()) || '---' }}</span></p>
                        <p class="text-gray-70">{{ __('Sale date') }}: <span class="text-black dark:text-white">{{
                            opportunity.date_closed || '---' }}</span></p>
                        <p class="text-gray-70">{{ __('Probability') }}: <span class="text-black dark:text-white">{{
                            opportunity.probability || '---' }}</span></p>
                        <p class="text-gray-70">{{ __('Expected amount') }}: <span class="text-black dark:text-white">{{
                            setCurrency(opportunity.amount_expected) || '---'
                                }}</span></p>
                        <p class="text-gray-70" v-if="opportunity.status == 'failed'">{{ __('The reason for the loss')
                        }}: <span class="text-black dark:text-white">{{
                                opportunity.failure_reason_title || '---' }}</span></p>
                        <p class="text-gray-70">{{ __('User') }}: <span class="text-black dark:text-white">{{
                            opportunity.user }}</span></p>
                        <p class="text-gray-70">{{ __('Created At') }}: <span class="text-black dark:text-white">{{
                            opportunity.created_at }}</span></p>
                        <p class="text-gray-70">{{ __('Update date') }}: <span class="text-black dark:text-white">{{
                            opportunity.updated_at }}</span></p>
                    </div>
                </div>
            </div>
            <div
                class="bg-white dark:bg-base-300 dark:shadow-whiteShadow rounded-xl shadow-sm collapse collapse-plus mt-4">
                <input type="checkbox" />
                <div class="collapse-title text-xl font-medium">
                    {{ __('More information') }}
                </div>
                <div class="collapse-content">
                    <div class="grid md:grid-cols-2 grid-cols-1 lg:grid-cols-3 md:gap-x-14 gap-y-4">
                        <p class="text-gray-70">{{ __('Lead source') }}: <span class="text-black dark:text-white">{{
                            opportunity.lead_source_title || '---'
                                }}</span></p>
                        <p class="text-gray-70">{{ __('Campaign') }}: <span class="text-black dark:text-white">{{
                            opportunity.campaign_name || '---'
                                }}</span></p>
                        <p class="text-gray-70">{{ __('Priority') }}: <span class="text-black dark:text-white">{{
                            __(opportunity.priority?.replace('_', ' ')?.capitalize()) || '---' }}</span></p>
                        <p class="text-gray-70">{{ __('Type') }}: <span class="text-black dark:text-white">{{
                            __(opportunity.type?.replace('_', ' ')?.capitalize()) || '---' }}</span></p>
                        <p class="text-gray-70">{{ __('Next step') }}: <span class="text-black dark:text-white">{{
                            opportunity.next_step || '---' }}</span></p>
                        <p class="text-gray-70">{{ __('Sales forecast') }}: <span class="text-black dark:text-white">{{
                            __(opportunity.forecast?.replace('_', ' ')?.capitalize()) || '---' }}</span></p>
                        <p class="text-gray-70">{{ __('Invoice sending periods') }}: <span
                                class="text-black dark:text-white">
                                {{ opportunity.invoicing_period ? __('Every') + ' ' + (opportunity.invoicing_period >=
                                    12 ?
                                    opportunity.invoicing_period / 12 : opportunity.invoicing_period) + ' ' +
                                    __(opportunity.invoicing_period >= 12 ? 'Year' : 'Month') : '---' }}
                            </span></p>
                        <p class="text-gray-70">{{ __('End date of the payment period') }}: <span
                                class="text-black dark:text-white">
                                {{ opportunity.recurring_end_date || '---' }}
                            </span></p>
                        <div class="divider h-auto col-span-full my-0"></div>
                        <p class="text-gray-70">{{ __('Description') }}: <span class="text-black dark:text-white">{{
                            opportunity.description || '---' }}</span></p>
                    </div>
                </div>
            </div>
            <div
                class="bg-white dark:bg-base-300 dark:shadow-whiteShadow rounded-xl shadow-sm collapse collapse-plus mt-4">
                <input type="checkbox" />
                <div class="collapse-title text-xl font-medium">
                    {{ __('Products') }}
                </div>
                <div class="collapse-content">
                    <TableProduct :products="opportunity.items" :currency="opportunity.currency" />
                    <div class="divider h-auto col-span-full" v-if="opportunity.items.length"></div>
                    <div class="overflow-x-auto col-span-full" v-if="opportunity.items.length">
                        <table class="table table-xs md:table-sm xl:table-md">
                            <thead>
                                <tr class="text-center">
                                    <th> {{ __('Total Amount') }} </th>
                                    <th> {{ __('Discount') }} </th>
                                    <th> {{ __('Final amount') }} </th>
                                </tr>

                            </thead>
                            <tbody>
                                <tr class="text-center">
                                    <td> {{ getTotal()?.currency(opportunity.currency) }} </td>
                                    <td> {{ getDiscount()?.currency(opportunity.currency) }} </td>
                                    <td> {{ opportunity.amount?.currency(opportunity.currency) }} </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
            <div v-if="opportunity.appointments?.length"
                class="bg-white dark:bg-base-300 dark:shadow-whiteShadow rounded-xl shadow-sm collapse-plus collapse mt-4">
                <input type="checkbox" />
                <div class="collapse-title text-xl font-medium">
                    {{ __('Activities') }}
                </div>
                <div class="collapse-content">
                    <TableAppointment :appointments="opportunity.appointments" @setItem="setItem" />
                </div>
            </div>
            <div v-if="opportunity.invoices?.length"
                class="bg-white dark:bg-base-300 dark:shadow-whiteShadow rounded-xl shadow-sm collapse-plus collapse mt-4">
                <input type="checkbox" />
                <div class="collapse-title text-xl font-medium">
                    {{ __('Invoices') }}
                </div>
                <div class="collapse-content">
                    <TableInvoice :invoices="opportunity.invoices" @setItem="setItem" />
                </div>
            </div>
            <div
                class="bg-white dark:bg-base-300 dark:shadow-whiteShadow rounded-xl shadow-sm collapse collapse-plus mt-4">
                <input type="checkbox" />
                <div class="collapse-title text-xl font-medium">
                    {{ __('Appendices') }}
                </div>
                <div class="collapse-content">
                    <div class="overflow-x-auto">
                        <table class="table table-xs md:table-sm xl:table-md">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>{{ __('File title') }}</th>
                                    <th>{{ __('File type') }}</th>
                                    <th>{{ __('Status') }}</th>
                                    <th>{{ __('Action') }}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(item, index) in opportunity.attachments">
                                    <th>{{ index + 1 }}</th>
                                    <th>{{ item.name }}</th>
                                    <td>{{ item.mime_type }}</td>
                                    <td>
                                        {{ __(item.url ? 'Uploaded' : 'Uploading') }}
                                    </td>
                                    <td>
                                        <a :href="item.url" target="_blank" class="btn btn-outline btn-info btn-sm">
                                            {{ __('Show') }}
                                        </a>
                                    </td>
                                </tr>
                            </tbody>
                            <tfoot class="text-center" v-if="!opportunity.attachments.length">
                                <tr>
                                    <td colspan="5">{{ __('No file found to display') }}</td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    </div>
    <Teleport to="#app">
        <input type="checkbox" id="add-appointment-modal" class="modal-toggle" />
        <div class="modal" for="add-appointment-modal">
            <div class="modal-box max-w-7xl bg-base-200 min-h-40" for="">
                <label @click="hideModal" class="btn btn-sm btn-circle btn-ghost absolute left-2 top-2">✕</label>
                <AddEditAppointment :appointment="appointment" v-if="modal == 'add-appointment-modal'"
                    modal="add-appointment-modal" />
            </div>
        </div>
    </Teleport>

    <Teleport to="#app">
        <input type="checkbox" id="add-invoice-modal" class="modal-toggle" />
        <div class="modal" for="add-invoice-modal">
            <div class="modal-box max-w-7xl bg-base-200 min-h-40" for="">
                <label @click="hideModal" class="btn btn-sm btn-circle btn-ghost absolute left-2 top-2">✕</label>
                <AddEditInvoice :invoice="invoice" v-if="modal == 'add-invoice-modal'" modal="add-invoice-modal" />
            </div>
        </div>
    </Teleport>

    <Teleport to="#app" v-if="showSidebar && opportunity.id">
        <NoteSidebar type="opportunity" :id="opportunity.id" :title="opportunity.name" @close="closeSidebar" />
    </Teleport>

    <ModalDelete id="opportunity-delete-modal" :title='__("Delete opportunity")'
        :content='__("Are you sure to delete this opportunity?")' @remove="removeOpportunity" />

    <ModalDelete id="appointment-delete-modal" :title="__('Delete appointment')"
        :content="__('Are you sure to delete this appointment?')" @remove="removeAppointmentItem" />

    <ModalDelete id="invoice-delete-modal" :title="__('Delete invoice')"
        :content="__('Are you sure to delete this invoice?')" @remove="removeInvoiceItem" />
</template>
<script setup>

import { computed, onMounted, reactive, ref, watch } from 'vue';
const { getOpportunity, opportunity, route, loading, meta, errors, remove, removeOpportunity, appointment, invoice, removeAppointment, removeInvoice } = useDashboard();
const setItem = (item) => Object.assign(remove, { id: item.id, single: true });
import { InformationCircleIcon } from '@heroicons/vue/24/outline';
const setCurrency = (value) => window.setCurrency(value, window.i18n[opportunity.currency?.capitalize()]);
const props = defineProps(['id']);
const modal = ref(null);
const showSidebar = ref(false);
const getData = () => {
    getOpportunity(props.id || route.params.id);
}
const openSidebar = () => showSidebar.value = true;

const closeSidebar = () => setTimeout(() => showSidebar.value = false,200);
const calcDiscount = (item) => {
    let amount = item.discount;
    if (item.discount && item.discount_type == 'percent') amount = item.discount * item.sale_price / 100;
    return amount;
}
const getDiscount = () => opportunity.items.reduce((carry, item) => carry + (calcDiscount(item) * item.quantity), 0);
const getTotal = () => opportunity.items?.reduce((carry, item) => carry + (item.sale_price * item.quantity), 0);
const setAppointment = () => {
    Object.assign(appointment, {
        parent_type: "opportunity",
        appointmentable_id: opportunity.id,
    });
    modal.value = 'add-appointment-modal';
    document.getElementById(modal.value).checked = true;
}
const removeAppointmentItem = () => {
    removeAppointment('appointment-delete-modal').then((res) => {
        if (res && !res.error) getData();
    });
}
const removeInvoiceItem = () => {
    removeInvoice('invoice-delete-modal').then((res) => {
        if (res && !res.error) getData();
    });
}
watch(progressDialog, (value) => {
    if (modal.value && !value && !errors.value) {
        modal.value = null;
        getData();
    }
})
const setInvoice = () => {
    Object.assign(invoice, {
        parent_type: "opportunity",
        invoiceable_id: opportunity.id,
        items: opportunity.items,
        currency: opportunity.currency,
        exchange_rate: opportunity.exchange_rate
    });
    modal.value = 'add-invoice-modal';
    document.getElementById(modal.value).checked = true;
}
const hideModal = () => {
    document.getElementById(modal.value).checked = false;
    modal.value = null;
}
onMounted(() => {
    getData();
});
</script>